import { ENDPOINTS, useCreatePayload } from 'hammock';
import { useMemo } from 'react';
import { useQueries } from 'react-query';

import { AnalyticQuery, QueryId, QueryParamUrlObject } from '@candu-ai/analytics/types';

import { createUseQuery } from './analytics-utils';
import { useQueryParams } from './useQueryParams';

type Item = {
  value: string;
  label: string;
};

export type QueryParamsHook = {
  queryId: QueryId;
  queryParams: QueryParamUrlObject;
  aggregateQueryId?: QueryId;
  selectedSegments?: Item[];
  contentId?: string;
  hasAggregateEndpoint?: boolean;
};
export const useGetTimeSeriesAndAggregate = <T>({
  queryId,
  selectedSegments,
  contentId,
  aggregateQueryId,
  queryParams,
}: QueryParamsHook) => {
  const { get } = useCreatePayload(ENDPOINTS.analyticQueries);

  const { queryParams: queryParamsFn, dataEntries } = useQueryParams({
    selectedSegments,
    contentId,
    dateRangeQueries: queryParams,
  });

  const seriePayload = dataEntries.map((entry) =>
    get({
      id: queryId,
      query: queryParamsFn(entry),
    }),
  );

  const aggregatePayload = dataEntries.map((entry) =>
    get({
      id: aggregateQueryId || '',
      query: queryParamsFn(entry),
    }),
  );

  const results = useQueries([
    ...seriePayload.map((p) => createUseQuery<T>(p)),
    ...aggregatePayload.map((p) => createUseQuery<T>(p, { enabled: !!aggregateQueryId })),
  ]);

  // slice the array in two.
  // we cannot use multiple useQueries otherwise test are not running well
  const seriesResult = results.slice(0, seriePayload.length);
  const aggregateResult = results.slice(
    seriePayload.length,
    seriePayload.length + aggregatePayload.length,
  );

  const [seriesData, aggregateData] = useMemo(() => {
    const seriesDataMemoized = seriesResult
      ?.map((res) => res?.data)
      .filter(Boolean) as AnalyticQuery<T>[];

    if (aggregateQueryId) {
      const aggregateDataMemoized = aggregateResult
        ?.map((res) => res?.data)
        .filter(Boolean) as AnalyticQuery<T>[];
      return [seriesDataMemoized, aggregateDataMemoized];
    }
    return [seriesDataMemoized, seriesDataMemoized];
  }, [seriesResult, aggregateResult, aggregateQueryId]);

  return [seriesData, aggregateData];
};
